import ErrorManager from '@/common/utils/ErrorManager';
import { sortValue } from '@/models/SortType';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import { RootState } from '@/plugins/store';
import { ActionTree } from 'vuex';
import DevisService, { FilterDevis } from '../services/DevisService';
import { DevisActions, DevisActionTypes } from './action-types';
import { DEVIS } from './mutation-types';
import { SuiviDevisState } from './state';
import { FilterDemandeIntervention } from '@/modules/DemandeIntervention/services/DemandeInterventionService';
import InterventionService from '@/modules/Intervention/services/InterventionService';
import { EQUIPEMENT } from '@/modules/Equipements/store/mutation-types';
import { Site } from '@/models/Site';
import dayjs from 'dayjs';

export const actions: ActionTree<SuiviDevisState, RootState> = {
    async [DevisActions.GET_DEVIS]({ commit }: DevisActionTypes, payload: FilterDevis) {
        commit(DEVIS.SET_ERROR, undefined);
        commit(DEVIS.SET_IS_LOADING, true);
        commit(DEVIS.SET_PAGE, payload.page);

        const { isBim, ...serviceParams } = payload;

        if (isBim) {
            commit(`equipementState/${EQUIPEMENT.DEVIS_SET_ERROR}`, undefined, { root: true });
            commit(`equipementState/${EQUIPEMENT.DEVIS_SET_IS_LOADING}`, true, { root: true });
            commit(`equipementState/${EQUIPEMENT.DEVIS_SET_PAGE}`, payload.page, { root: true });
        }

        try {
            const response = await DevisService.getListDevis(serviceParams);

            if (response.status === 200 && response.data !== undefined) {
                if (payload.page === 0) {
                    commit(DEVIS.SET_ITEMS, response.data);
                    commit(DEVIS.SET_NB_OF_PAGES, response.headers.pages);
                    commit(DEVIS.SET_COUNT, response.headers.count);
                    if (isBim) {
                        commit(`equipementState/${EQUIPEMENT.DEVIS_SET_ITEMS}`, response.data, { root: true });
                        commit(`equipementState/${EQUIPEMENT.DEVIS_SET_NB_OF_PAGES}`, response.headers.pages, {
                            root: true,
                        });
                        commit(`equipementState/${EQUIPEMENT.DEVIS_SET_COUNT}`, response.headers.count, { root: true });
                    }
                } else {
                    commit(DEVIS.ADD_ITEMS, response.data);
                    if (isBim) {
                        commit(`equipementState/${EQUIPEMENT.DEVIS_ADD_ITEMS}`, response.data, { root: true });
                    }
                }
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
            commit(DEVIS.SET_IS_LOADING, false);
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.DEVIS_SET_IS_LOADING}`, false, { root: true });
            }
        } catch (error) {
            commit(DEVIS.SET_IS_LOADING, false);
            commit(DEVIS.SET_ERROR, error);
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.DEVIS_SET_IS_LOADING}`, false, { root: true });
                commit(`equipementState/${EQUIPEMENT.DEVIS_SET_ERROR}`, error, { root: true });
            }
        }
    },

    async [DevisActions.GET_INTERVENTIONS]({ commit }: DevisActionTypes, payload: FilterDemandeIntervention) {
        commit(DEVIS.SET_ERROR_INTERVENTIONS, undefined);
        commit(DEVIS.SET_IS_LOADING_INTERVENTIONS, true);
        commit(DEVIS.SET_PAGE_INTERVENTIONS, payload.page);

        try {
            const response = await InterventionService.getNonPreventiveInterventionList(payload);

            if (response.status === 200 && response.data !== undefined) {
                if (payload.page === 0) {
                    commit(DEVIS.SET_ITEMS_INTERVENTIONS, response.data);
                    commit(DEVIS.SET_NB_OF_PAGES_INTERVENTIONS, response.headers.pages);
                    commit(DEVIS.SET_COUNT_INTERVENTIONS, response.headers.count);
                } else {
                    commit(DEVIS.ADD_ITEMS_INTERVENTIONS, response.data);
                }
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
            commit(DEVIS.SET_IS_LOADING_INTERVENTIONS, false);
        } catch (error) {
            commit(DEVIS.SET_IS_LOADING_INTERVENTIONS, false);
            commit(DEVIS.SET_ERROR_INTERVENTIONS, error);
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        }
    },

    async [DevisActions.NAV_FILTER]({ commit }: DevisActionTypes, filter: string[] | { status: string[]; sites: Site[] }) {
        const status = Array.isArray(filter) ? filter : filter.status;
        const sites = Array.isArray(filter) ? [] : filter.sites;
        const dateDebut = dayjs().add(-3, 'month').format('YYYY-MM-DD');
        const dateFin = dayjs().format('YYYY-MM-DD');
        commit(DEVIS.SET_FILTER_STATUS, status);
        commit(DEVIS.SET_FILTER_SITE, sites);
        commit(DEVIS.SET_FILTER_DATE_DEBUT, dateDebut);
        commit(DEVIS.SET_FILTER_DATE_FIN, dateFin);
        commit(DEVIS.RESET_ITEMS);
        this.dispatch(DevisActions.GET_DEVIS, {
            page: 0,
            chantierIds: sites.map(site => site.chantierId),
            dateDebut,
            dateFin,
            etatDevis: this.state.devisModuleState.filterStatus,
            sort:
                this.state.devisModuleState.sort &&
                sortValue(this.state.devisModuleState.sort.sortData, this.state.devisModuleState.sort.sortStatus),
        });
    },
    async [DevisActions.EXPORT_XLS]({ commit }: DevisActionTypes, payload: FilterDevis) {
        const { isBim, ...params } = payload;
        const serviceParams = params;
        delete (serviceParams as any).isBim;
        if (isBim) {
            commit(`equipementState/${EQUIPEMENT.DEVIS_SET_EXPORT_XLS_IS_LOADING}`, true, { root: true });
        } else {
            commit(DEVIS.SET_EXPORT_XLS_IS_LOADING, true);
        }
        try {
            const response = await DevisService.getExportXlsDevis(serviceParams);
            if (response.status === 200 && response.data !== undefined) {
                const blob = new Blob([response.data], { type: response.headers['content-type'] });
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = 'export_devis.xlsx';
                link.click();
                URL.revokeObjectURL(link.href);
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.DEVIS_SET_EXPORT_XLS_IS_LOADING}`, false, {
                    root: true,
                });
            } else {
                commit(DEVIS.SET_EXPORT_XLS_IS_LOADING, false);
            }
        } catch (error) {
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.DEVIS_SET_EXPORT_XLS_IS_LOADING}`, false, {
                    root: true,
                });
            } else {
                commit(DEVIS.SET_EXPORT_XLS_IS_LOADING, false);
            }
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        }
    },
};
